import { CdkAccordionModule } from '@angular/cdk/accordion';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialogModule } from '@angular/material/dialog';
import { RouterModule, Routes } from '@angular/router';
import { SharedFeatureComponentsModule } from '@digilize/shared/feature/components/src';
import { SharedFeatureModulesAlertModule } from '@digilize/shared/feature/modules/alert/src';
import { LoggedInGuard } from '@digilize/shared/feature/modules/auth/src/lib/logged-in.guard';
import { LangComponentsModule } from '@digilize/shared/feature/modules/i18n/src';
import { SharedUiComponentsModule } from '@digilize/shared/ui/components/src';
import { SharedUiMaterialModule } from '@digilize/shared/ui/material/src';
import { SharedUtilsDirectivesModule } from '@digilize/shared/utils/directives/src';
import { TranslateModule } from '@ngx-translate/core';
import player from 'lottie-web';
import { InlineSVGModule } from 'ng-inline-svg-2';
import { NgxGlideModule } from 'ngx-glide';
import { LottieModule } from 'ngx-lottie';

import { PersonalDataComponent } from '../components/personal-data/personal-data.component';
import { ProfilePreviewComponent } from '../components/profile-preview/profile-preview.component';
import { ProfileVisibilityComponent } from '../components/profile-visibility/profile-visibility.component';
import { ShareModalComponent } from '../components/share-modal/share-modal.component';
import { SharedModule } from '../components/shared.module';
import { SkillsCompletionComponent } from '../components/skills-completion/skills-completion.component';
import { SkillsReorderDialogComponent } from '../components/skills-reorder-dialog/skills-reorder-dialog.component';
import { VacancyApplyComponent } from '../components/vacancy-apply/vacancy-apply.component';
import { WorkPreferencesComponent } from '../components/work-preferences/work-preferences.component';
import { UnsavedChangesGuard } from '../guards/unsaved-changes.guard';
import { PipesModule } from '../pipes/pipes.module';
import { AddYourEducationComponent } from './have-resume/add-your-education/add-your-education.component';
import { CreateAccountComponent } from './have-resume/create-account/create-account.component';
import { CvSkillsComponent } from './have-resume/cv-skills/cv-skills.component';
import { DesiredOccupationsComponent } from './have-resume/desired-occupations/desired-occupations.component';
import { EducationLevelSelectionComponent } from './have-resume/education-level-selection/education-level-selection.component';
import { GeneralInformationComponent } from './have-resume/general-information/general-information.component';
import { HaveResumeComponent } from './have-resume/have-resume.component';
import { ItSkillsComponent } from './have-resume/it-skills/it-skills.component';
import { KnowledgeSkillsComponent } from './have-resume/knowledge-skills/knowledge-skills.component';
import { LanguagesKnowledgeComponent } from './have-resume/languages-knowledge/languages-knowledge.component';
import { ProfessionSkillsComponent } from './have-resume/profession-skills/profession-skills.component';
import { ProfessionalSkillsComponent } from './have-resume/professional-skills/professional-skills.component';
import { SearchAddSkillsComponent } from './have-resume/search-add-skills/search-add-skills.component';
import { SelectYourJobComponent } from './have-resume/select-your-job/select-your-job.component';
import { SkillsSummaryComponent } from './have-resume/skills-summary/skills-summary.component';
import { SoftSkillsComponent } from './have-resume/soft-skills/soft-skills.component';
import { TasksComponent } from './have-resume/tasks/tasks.component';
import { UploadYourCvComponent } from './have-resume/upload-your-cv/upload-your-cv.component';
import { UploadedCvComponent } from './have-resume/uploaded-cv/uploaded-cv.component';
import { CompaniesComponent } from './my-profile/companies/companies.component';
import { CompanyCardComponent } from './my-profile/companies/components/company-card/company-card.component';
import { CompaniesForYouComponent } from './my-profile/companies/pages/companies-for-you/companies-for-you.component';
import { CompanyDetailComponent } from './my-profile/companies/pages/company-detail/company-detail.component';
import { CompanyVacanciesComponent } from './my-profile/companies/pages/company-vacancies/company-vacancies.component';
import { DiscoverCompaniesComponent } from './my-profile/companies/pages/discover-companies/discover-companies.component';
import { FollowedCompaniesComponent } from './my-profile/companies/pages/followed-companies/followed-companies.component';
import { NewCompaniesComponent } from './my-profile/companies/pages/new-companies/new-companies.component';
import { SubNavigationComponent } from './my-profile/components/sub-navigation/sub-navigation.component';
import { EducationCardComponent } from './my-profile/educations/components/education-card/education-card.component';
import { EducationsComponent } from './my-profile/educations/educations.component';
import { EducationDetailComponent } from './my-profile/educations/pages/education-detail/education-detail.component';
import { EducationSearchComponent } from './my-profile/educations/pages/education-search/education-search.component';
import { EducationSkillComponent } from './my-profile/educations/pages/education-skill/education-skill.component';
import { EducationsListComponent } from './my-profile/educations/pages/educations-list/educations-list.component';
import { SavedEducationsComponent } from './my-profile/educations/pages/saved-educations/saved-educations.component';
import { MyProfileComponent } from './my-profile/my-profile.component';
import { ProfessionCardComponent } from './my-profile/professions/components/profession-card/profession-card.component';
import { ProfessionDetailComponent } from './my-profile/professions/pages/profession-detail/profession-detail.component';
import { ProfessionSearchComponent } from './my-profile/professions/pages/profession-search/profession-search.component';
import { ProfessionsListComponent } from './my-profile/professions/pages/professions-list/professions-list.component';
import { SavedProfessionsComponent } from './my-profile/professions/pages/saved-professions/saved-professions.component';
import { ProfessionsComponent } from './my-profile/professions/professions.component';
import { ProfileCheckComponent } from './my-profile/profile-check/profile-check.component';
import { VacancyCardComponent } from './my-profile/vacancies/components/vacancy-card/vacancy-card.component';
import { SavedVacanciesComponent } from './my-profile/vacancies/pages/saved-vacancies/saved-vacancies.component';
import { VacanciesListComponent } from './my-profile/vacancies/pages/vacancies-list/vacancies-list.component';
import { VacancyDetailComponent } from './my-profile/vacancies/pages/vacancy-detail/vacancy-detail.component';
import { VacancySearchComponent } from './my-profile/vacancies/pages/vacancy-search/vacancy-search.component';
import { VacanciesComponent } from './my-profile/vacancies/vacancies.component';
import { SharedProfileComponent } from './shared-profile/shared-profile.component';

const routes: Routes = [
  {
    path: 'login',
    loadChildren: () => import('./start/start.module').then((m) => m.StartModule),
  },
  {
    path: 'auth',
    loadChildren: () => import('./auth/auth.module').then((m) => m.AuthModule),
  },
  {
    path: 'start-user-flow',
    component: HaveResumeComponent,
  },
  {
    path: 'profile-check',
    component: MyProfileComponent,
    children: [
      {
        path: '',
        component: ProfileCheckComponent,
        canDeactivate: [UnsavedChangesGuard],
      },
      {
        path: 'vacancies',
        component: VacanciesComponent,
        children: [
          { path: '', component: VacanciesListComponent },
          { path: 'search', component: VacancySearchComponent },
          { path: 'saved', component: SavedVacanciesComponent },
        ],
      },
      {
        path: 'professions',
        component: ProfessionsComponent,
        children: [
          { path: '', component: ProfessionsListComponent },
          { path: 'search', component: ProfessionSearchComponent },
          { path: 'saved', component: SavedProfessionsComponent },
          { path: ':id', component: ProfessionDetailComponent },
        ],
      },
      {
        path: 'companies',
        component: CompaniesComponent,
        children: [
          { path: '', redirectTo: 'for-you', pathMatch: 'full' },
          { path: 'for-you', component: CompaniesForYouComponent },
          { path: 'discover', component: DiscoverCompaniesComponent },
          { path: 'new', component: NewCompaniesComponent },
          { path: 'followed', component: FollowedCompaniesComponent },
          { path: ':id', component: CompanyDetailComponent },
          { path: ':id/vacancies', component: CompanyVacanciesComponent },
        ],
      },
      {
        path: 'educations',
        component: EducationsComponent,
        children: [
          { path: '', component: EducationsListComponent },
          { path: 'search', component: EducationSearchComponent },
          { path: 'saved', component: SavedEducationsComponent },
          { path: 'skill/:skillName', component: EducationSkillComponent },
          { path: ':id', component: EducationDetailComponent },
        ],
      },
    ],
    canActivate: [LoggedInGuard],
    data: { customLoginRedirectRoute: '/login' },
  },
  {
    path: 'shared-profile',
    component: SharedProfileComponent,
  },
  {
    path: 'offers',
    loadChildren: () => import('./offers/offers.module').then((m) => m.OffersModule),
  },
  {
    path: 'terms-and-conditions',
    loadChildren: () =>
      import('./terms-and-conditions/terms-and-conditions.module').then((m) => m.TermsAndConditionsModule),
  },
  {
    path: 'privacy-and-cookies',
    loadChildren: () =>
      import('./privacy-and-cookies/privacy-and-cookies.module').then((m) => m.PrivacyAndCookiesModule),
  },
  {
    path: '',
    redirectTo: 'login',
    pathMatch: 'full',
  },
  {
    path: '**',
    redirectTo: 'login',
  },
];

@NgModule({
  declarations: [
    HaveResumeComponent,
    UploadYourCvComponent,
    UploadedCvComponent,
    SelectYourJobComponent,
    DesiredOccupationsComponent,
    CvSkillsComponent,
    EducationLevelSelectionComponent,
    ProfessionSkillsComponent,
    AddYourEducationComponent,
    SoftSkillsComponent,
    KnowledgeSkillsComponent,
    ProfessionalSkillsComponent,
    ItSkillsComponent,
    LanguagesKnowledgeComponent,
    SearchAddSkillsComponent,
    GeneralInformationComponent,
    SkillsSummaryComponent,
    CreateAccountComponent,
    MyProfileComponent,
    ProfileCheckComponent,
    TasksComponent,
    PersonalDataComponent,
    SkillsCompletionComponent,
    WorkPreferencesComponent,
    VacancyApplyComponent,
    ProfileVisibilityComponent,
    ShareModalComponent,
    ProfilePreviewComponent,
    SkillsReorderDialogComponent,
    VacanciesComponent,
    VacancyCardComponent,
    VacanciesListComponent,
    VacancySearchComponent,
    VacancyDetailComponent,
    SavedVacanciesComponent,
    ProfessionsComponent,
    ProfessionCardComponent,
    ProfessionsListComponent,
    ProfessionSearchComponent,
    ProfessionDetailComponent,
    SavedProfessionsComponent,
    CompaniesComponent,
    CompanyCardComponent,
    CompaniesForYouComponent,
    DiscoverCompaniesComponent,
    NewCompaniesComponent,
    FollowedCompaniesComponent,
    CompanyDetailComponent,
    CompanyVacanciesComponent,
    EducationsComponent,
    EducationCardComponent,
    EducationsListComponent,
    EducationSearchComponent,
    EducationDetailComponent,
    EducationSkillComponent,
    SavedEducationsComponent,
    SubNavigationComponent,
    SharedProfileComponent,
  ],
  imports: [
    CommonModule,
    FormsModule,
    SharedUiMaterialModule,
    CdkAccordionModule,
    SharedUiComponentsModule,
    RouterModule.forChild(routes),
    InlineSVGModule,
    TranslateModule,
    SharedModule,
    SharedFeatureComponentsModule,
    MatDialogModule,
    NgxGlideModule,
    PipesModule,
    SharedFeatureModulesAlertModule,
    LottieModule.forRoot({ player: () => player }),
    LangComponentsModule,
    SharedUtilsDirectivesModule,
  ],
  exports: [HaveResumeComponent],
})
export class MainModule {}
